import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'next-i18next';
import '../consts/events.quiz-classroom-quiz';
import { trackEvent } from '@zep/logger/tracking';
import { zusToast } from '@zep/toast';
import { useDeleteClassLink, useGetMyClassGroups as useGetMyClassrooms, useLinkContent } from '@/api/gen/classroom/classroom.gen';
import { useJoinWithLocale } from '@/hooks';
import { useUser } from '@/lib/auth';
import { selectData } from '@/shared/utils';
import { ClassroomContentLinkedToast } from '../components/ClassroomContentLinkedToast';
import { type ContentShareType } from '../types';
import { classroomContentToken, type ClassroomSource, handleClassroomLinkError, invalidateClassroomMutationQueries, trackClassroomEvent } from '../utils';
interface UseClassroomLinkingParams {
  contentId: string;
  contentType: ContentShareType;
  source?: ClassroomSource;
}
const getErrorStatus = (error: unknown) => {
  if (error && typeof error === 'object' && 'status' in error) {
    return String(error.status);
  }
  return undefined;
};

/**
 * 내 클래스 목록 + content link 상태 + link/unlink mutation을 한 번에 제공.
 *
 * `useGetMyClassrooms` 에 `linkedContentId`/`linkedContentType` 를 전달하면 응답
 * 각 classroom 에 `isLinked`/`linkedClassLinkId` 가 채워져 오므로 별도 per-classroom
 * `getClassLinks` 호출 없이 1회 요청으로 link 상태를 알 수 있다.
 *
 * 한 모달에서 여러 classroom 에 대해 mutate 하므로 wrapper hook(classroomId 인자
 * 고정형) 대신 raw mutation 을 쓰되, invalidation 면은 mutation wrapper 와 같이
 * `invalidateClassroomMutationQueries` 로 통일한다.
 */
export function useClassroomLinking({
  contentId,
  contentType,
  source = 'classroom_link_modal'
}: UseClassroomLinkingParams) {
  const queryClient = useQueryClient();
  const contentToken = classroomContentToken(contentType);
  const {
    t
  } = useTranslation(['common']);
  const {
    isAuthenticated,
    isInitialized,
    user
  } = useUser();
  const canLoadClassrooms = isAuthenticated && user?.type?.toLowerCase() !== 'student';
  const {
    data: classrooms = [],
    isLoading: isLoadingClassrooms
  } = useGetMyClassrooms({
    sortBy: 'updatedAt',
    order: 'desc',
    linkedContentId: contentId,
    linkedContentType: contentType
  }, {
    query: {
      enabled: canLoadClassrooms,
      select: selectData
    }
  });
  const classroomPath = useJoinWithLocale();
  const trackQuizLinkSave = ({
    classroomId,
    operation,
    result,
    reason
  }: {
    classroomId: string;
    operation: 'link' | 'unlink';
    result: 'success' | 'fail';
    reason?: string;
  }) => {
    if (contentType !== 'QUIZSET') return;
    trackEvent('quiz_classroom_quiz_save', {
      classroom_id: classroomId,
      quiz_set_id: contentId,
      operation,
      result,
      place: source,
      ...(reason && {
        reason
      })
    });
  };
  const linkMutation = useLinkContent({
    mutation: {
      onSuccess: (_data, variables) => {
        trackQuizLinkSave({
          classroomId: variables.classGroupId,
          operation: 'link',
          result: 'success'
        });
        trackClassroomEvent(`success_link_classroom_${contentToken}`, {
          classroomId: variables.classGroupId,
          contentId,
          source
        });
        zusToast.custom(<ClassroomContentLinkedToast classroomId={variables.classGroupId} contentType={contentType} classroomPath={classroomPath} />, {
          duration: 2500,
          position: 'top-center'
        });
        return invalidateClassroomMutationQueries(queryClient, variables.classGroupId);
      },
      onError: (error, variables) => {
        trackQuizLinkSave({
          classroomId: variables.classGroupId,
          operation: 'link',
          result: 'fail',
          reason: getErrorStatus(error)
        });
        trackClassroomEvent(`fail_link_classroom_${contentToken}`, {
          classroomId: variables.classGroupId,
          contentId,
          source,
          errorStatus: getErrorStatus(error)
        });
        void handleClassroomLinkError(error, key => t(key as any));
      }
    }
  });
  const unlinkMutation = useDeleteClassLink({
    mutation: {
      onSuccess: (_data, variables) => {
        trackQuizLinkSave({
          classroomId: variables.classGroupId,
          operation: 'unlink',
          result: 'success'
        });
        trackClassroomEvent(`success_unlink_classroom_${contentToken}`, {
          classroomId: variables.classGroupId,
          contentId,
          source
        });
        return invalidateClassroomMutationQueries(queryClient, variables.classGroupId);
      },
      onError: (error, variables) => {
        trackQuizLinkSave({
          classroomId: variables.classGroupId,
          operation: 'unlink',
          result: 'fail',
          reason: getErrorStatus(error)
        });
        trackClassroomEvent(`fail_unlink_classroom_${contentToken}`, {
          classroomId: variables.classGroupId,
          contentId,
          source,
          errorStatus: getErrorStatus(error)
        });
      }
    }
  });
  const linkClassroom = async (classroomId: string) => {
    trackClassroomEvent(`click_link_classroom_${contentToken}`, {
      classroomId,
      contentId,
      source
    });

    // 성공·실패 처리(트래킹·토스트·쿼리 무효화)는 linkMutation 의 onSuccess/onError 가
    // 맡는다 — onSuccess 가 무효화 Promise 를 돌려줘 그때까지 isPending 이 유지되는
    // 계약이라 호출부로 끌어내지 않는다. 여기서는 reject 가 새지 않게만 받는다.
    try {
      await linkMutation.mutateAsync({
        classGroupId: classroomId,
        data: {
          contentId,
          contentType
        }
      });
    } catch {
      // onError 에서 이미 처리했다.
    }
  };
  const unlinkClassroom = async (classroomId: string, classLinkId: string) => {
    trackClassroomEvent(`click_unlink_classroom_${contentToken}`, {
      classroomId,
      contentId,
      source
    });

    // link 와 같은 이유로 성공·실패 처리는 unlinkMutation 의 콜백이 맡는다.
    try {
      await unlinkMutation.mutateAsync({
        classGroupId: classroomId,
        classLinkId
      });
    } catch {
      // onError 에서 이미 처리했다.
    }
  };
  return {
    classrooms,
    isLoadingClassrooms,
    isInitialized,
    canLoadClassrooms,
    isPending: linkMutation.isPending || unlinkMutation.isPending,
    linkClassroom,
    unlinkClassroom
  };
}